import { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { MapPin, Mail, Menu, X, ChevronDown, Sparkles, Syringe, Scissors, Heart, Droplets, ArrowLeft, Navigation as NavigationIcon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Studio Images
const STUDIO_IMAGES = {
  reception: "https://customer-assets.emergentagent.com/job_aesthetic-med-1/artifacts/6wtzf1ik_ef94d641-2ebe-4c55-9fa9-18de1a8f794e.jpeg",
  waiting: "https://customer-assets.emergentagent.com/job_aesthetic-med-1/artifacts/xnitzvlc_bd04f166-cb33-4fc8-95f1-ca4aab432e58.jpeg"
};

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#behandlungen", label: "Behandlungen" },
    { href: "#ueber-mich", label: "Über Mich" },
    { href: "#galerie", label: "Galerie" },
    { href: "#kontakt", label: "Kontakt" }
  ];

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? "glass shadow-sm py-4" : "bg-transparent py-6"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <a href="#" data-testid="logo-link" className="flex flex-col">
          <span className="font-['Playfair_Display'] text-xl md:text-2xl font-medium text-[#1C1917]">
            Medical Beauty
          </span>
          <span className="text-xs tracking-[0.3em] text-[#D4AF37] uppercase">NM</span>
        </a>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-10">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`nav-${link.href.slice(1)}`}
              className="nav-link"
            >
              {link.label}
            </a>
          ))}
          <a
            href="#kontakt"
            data-testid="nav-termin-btn"
            className="btn-primary"
          >
            Termin anfragen
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button
          data-testid="mobile-menu-btn"
          className="md:hidden p-2"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden absolute top-full left-0 right-0 bg-white shadow-lg py-6 px-6"
        >
          <div className="flex flex-col gap-4">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`mobile-nav-${link.href.slice(1)}`}
                className="nav-link py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <a
              href="#kontakt"
              data-testid="mobile-nav-termin-btn"
              className="btn-primary text-center mt-4"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Termin anfragen
            </a>
          </div>
        </motion.div>
      )}
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section data-testid="hero-section" className="min-h-screen relative flex items-center">
      <div className="absolute inset-0 bg-gradient-to-b from-[#FDFBF7] via-[#FDFBF7] to-[#F5F2EB]" />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-32 relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="order-2 md:order-1"
          >
            <motion.p variants={fadeInUp} className="section-subtitle mb-4">
              Praxis für ästhetische Medizin
            </motion.p>
            <motion.h1
              variants={fadeInUp}
              className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-medium text-[#1C1917] leading-tight mb-6"
            >
              Ihre Schönheit in<br />
              <span className="text-[#D4AF37]">medizinischen Händen</span>
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="text-base md:text-lg text-[#57534E] leading-relaxed mb-8 max-w-lg"
            >
              Exklusive ästhetische Behandlungen in Mertingen. 
              Individuell auf Sie abgestimmt, mit höchster medizinischer Expertise.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
              <a href="#kontakt" data-testid="hero-cta-primary" className="btn-primary">
                Termin vereinbaren
              </a>
              <a href="#behandlungen" data-testid="hero-cta-secondary" className="btn-secondary">
                Behandlungen entdecken
              </a>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="order-1 md:order-2"
          >
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-24 h-24 border border-[#D4AF37] opacity-50" />
              <img
                src={STUDIO_IMAGES.reception}
                alt="Medical Beauty NM Empfang"
                className="w-full h-[400px] md:h-[500px] object-cover"
                data-testid="hero-image"
              />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-[#D4AF37] opacity-20" />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 scroll-indicator">
        <ChevronDown size={32} className="text-[#D4AF37]" />
      </div>
    </section>
  );
};

// Treatments Section
const TreatmentsSection = ({ treatments }) => {
  const [activeCategory, setActiveCategory] = useState("Hyaluron");

  const categories = [
    { name: "Hyaluron", icon: <Sparkles size={20} /> },
    { name: "Botox", icon: <Syringe size={20} /> },
    { name: "PRP Behandlung", icon: <Heart size={20} /> },
    { name: "Skin & Regeneration", icon: <Sparkles size={20} /> },
    { name: "Fadenlifting", icon: <Scissors size={20} /> },
    { name: "Infusionstherapie", icon: <Droplets size={20} /> }
  ];

  const categoryDescriptions = {
    "Infusionstherapie": "Unsere Infusionstherapien versorgen den Körper direkt über die Blutbahn mit wichtigen Vitaminen, Mineralstoffen und Antioxidantien. Dadurch können Nährstoffe schneller und effektiver aufgenommen werden als über die normale Ernährung."
  };

  const filteredTreatments = treatments.filter(t => t.category === activeCategory);

  return (
    <section id="behandlungen" data-testid="treatments-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.p variants={fadeInUp} className="section-subtitle mb-4">
            Unsere Leistungen
          </motion.p>
          <motion.h2 variants={fadeInUp} className="section-title">
            Behandlungen
          </motion.h2>
          <motion.div variants={fadeInUp} className="gold-line mx-auto mt-6" />
        </motion.div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-2 md:gap-4 mb-12">
          {categories.map((cat) => (
            <button
              key={cat.name}
              data-testid={`category-${cat.name.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => setActiveCategory(cat.name)}
              className={`flex items-center gap-2 px-4 md:px-6 py-3 text-sm tracking-wider uppercase transition-all duration-300 ${
                activeCategory === cat.name
                  ? "bg-[#1C1917] text-white"
                  : "bg-transparent text-[#57534E] hover:text-[#1C1917] border border-stone-200"
              }`}
            >
              {cat.icon}
              <span className="hidden sm:inline">{cat.name}</span>
              <span className="sm:hidden">{cat.name.split(' ')[0]}</span>
            </button>
          ))}
        </div>

        {/* Category Description */}
        {categoryDescriptions[activeCategory] && (
          <motion.p
            key={`desc-${activeCategory}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center text-[#57534E] max-w-3xl mx-auto mb-12 leading-relaxed"
          >
            {categoryDescriptions[activeCategory]}
          </motion.p>
        )}

        {/* Treatments Grid */}
        <motion.div
          key={activeCategory}
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {filteredTreatments.map((treatment) => (
            <motion.div
              key={treatment.id}
              variants={fadeInUp}
              data-testid={`treatment-card-${treatment.id}`}
              className="treatment-card"
            >
              <h3 className="font-['Playfair_Display'] text-xl mb-2 text-[#1C1917]">
                {treatment.name}
              </h3>
              <p className="text-[#D4AF37] font-medium text-lg mb-2">
                {treatment.price}
              </p>
              {treatment.description && (
                <p className="text-sm text-[#57534E] leading-relaxed">
                  {treatment.description}
                </p>
              )}
            </motion.div>
          ))}
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mt-12 text-[#57534E]"
        >
          Individuelle Beratung für Ihre persönlichen Wünsche
        </motion.p>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="ueber-mich" data-testid="about-section" className="py-24 md:py-32 bg-[#FDFBF7]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="absolute top-4 left-4 w-full h-full border border-[#D4AF37] -z-10" />
            <img
              src={STUDIO_IMAGES.waiting}
              alt="Medical Beauty NM Wartebereich"
              className="w-full h-[400px] md:h-[500px] object-cover"
              data-testid="about-image"
            />
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.p variants={fadeInUp} className="section-subtitle mb-4">
              Ihre Ärztin
            </motion.p>
            <motion.h2 variants={fadeInUp} className="section-title mb-6">
              Ntiana Meziridou
            </motion.h2>
            <motion.div variants={fadeInUp} className="gold-line mb-8" />
            <motion.p variants={fadeInUp} className="text-[#57534E] leading-relaxed mb-6">
              Als erfahrene Ärztin für ästhetische Medizin ist es mein Ziel, 
              Ihre natürliche Schönheit zu unterstreichen. Mit modernsten Techniken 
              und einem geschulten Auge für Harmonie und Proportion schaffe ich 
              Ergebnisse, die Ihr Selbstbewusstsein stärken.
            </motion.p>
            <motion.p variants={fadeInUp} className="text-[#57534E] leading-relaxed mb-8">
              In meiner Praxis in Mertingen verbinde ich medizinische Expertise 
              mit einem luxuriösen Ambiente, damit Sie sich von der ersten Minute 
              an wohlfühlen. Jede Behandlung wird individuell auf Ihre Bedürfnisse 
              abgestimmt.
            </motion.p>
            <motion.a
              variants={fadeInUp}
              href="#kontakt"
              data-testid="about-cta"
              className="btn-secondary inline-block"
            >
              Beratung vereinbaren
            </motion.a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Gallery Section
const GallerySection = () => {
  return (
    <section id="galerie" data-testid="gallery-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.p variants={fadeInUp} className="section-subtitle mb-4">
            Einblicke
          </motion.p>
          <motion.h2 variants={fadeInUp} className="section-title">
            Unsere Praxis
          </motion.h2>
          <motion.div variants={fadeInUp} className="gold-line mx-auto mt-6" />
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative group overflow-hidden"
          >
            <img
              src={STUDIO_IMAGES.reception}
              alt="Empfangsbereich Medical Beauty NM"
              className="w-full h-[300px] md:h-[400px] object-cover transition-transform duration-700 group-hover:scale-105"
              data-testid="gallery-image-1"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917]/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-6">
              <span className="text-white font-['Playfair_Display'] text-xl">Empfangsbereich</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative group overflow-hidden"
          >
            <img
              src={STUDIO_IMAGES.waiting}
              alt="Wartebereich Medical Beauty NM"
              className="w-full h-[300px] md:h-[400px] object-cover transition-transform duration-700 group-hover:scale-105"
              data-testid="gallery-image-2"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917]/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-6">
              <span className="text-white font-['Playfair_Display'] text-xl">Wartebereich</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    treatment_interest: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      setSubmitStatus({ success: true, message: response.data.message });
      setFormData({ name: "", email: "", phone: "", message: "", treatment_interest: "" });
    } catch (error) {
      setSubmitStatus({
        success: false,
        message: "Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="kontakt" data-testid="contact-section" className="py-24 md:py-32 bg-[#F5F2EB]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Contact Info */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.p variants={fadeInUp} className="section-subtitle mb-4">
              Kontakt
            </motion.p>
            <motion.h2 variants={fadeInUp} className="section-title mb-6">
              Vereinbaren Sie<br />Ihren Termin
            </motion.h2>
            <motion.div variants={fadeInUp} className="gold-line mb-8" />
            <motion.p variants={fadeInUp} className="text-[#57534E] leading-relaxed mb-10">
              Ich freue mich darauf, Sie persönlich kennenzulernen und 
              gemeinsam Ihre individuellen Wünsche zu besprechen.
            </motion.p>

            <motion.div variants={fadeInUp} className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white flex items-center justify-center">
                  <MapPin className="text-[#D4AF37]" size={20} />
                </div>
                <div className="flex-1">
                  <h4 className="font-['Playfair_Display'] text-lg text-[#1C1917] mb-1">Adresse</h4>
                  <p className="text-[#57534E] mb-3">
                    An der Schmutter 16<br />
                    86690 Mertingen
                  </p>
                  <a
                    href="https://www.google.com/maps/dir/?api=1&destination=An+der+Schmutter+16%2C+86690+Mertingen"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="google-maps-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-[#D4AF37] text-[#1C1917] text-xs tracking-[0.15em] uppercase hover:bg-[#D4AF37] hover:text-white transition-all duration-300"
                  >
                    <NavigationIcon size={14} />
                    <span>Route planen</span>
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white flex items-center justify-center">
                  <Mail className="text-[#D4AF37]" size={20} />
                </div>
                <div>
                  <h4 className="font-['Playfair_Display'] text-lg text-[#1C1917] mb-1">E-Mail</h4>
                  <a 
                    href="mailto:info@medicalbeauty-nm.de" 
                    data-testid="email-link"
                    className="text-[#57534E] hover:text-[#D4AF37] transition-colors"
                  >
                    info@medicalbeauty-nm.de
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-white p-8 md:p-12"
          >
            <h3 className="font-['Playfair_Display'] text-2xl text-[#1C1917] mb-8">
              Kontaktformular
            </h3>

            {submitStatus && (
              <div
                data-testid="form-status"
                className={`p-4 mb-6 ${
                  submitStatus.success
                    ? "bg-green-50 text-green-800 border border-green-200"
                    : "bg-red-50 text-red-800 border border-red-200"
                }`}
              >
                {submitStatus.message}
              </div>
            )}

            <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
              <div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Ihr Name *"
                  required
                  data-testid="input-name"
                  className="form-input"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="E-Mail *"
                  required
                  data-testid="input-email"
                  className="form-input"
                />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Telefon"
                  data-testid="input-phone"
                  className="form-input"
                />
              </div>

              <div>
                <select
                  name="treatment_interest"
                  value={formData.treatment_interest}
                  onChange={handleChange}
                  data-testid="input-treatment"
                  className="form-input cursor-pointer"
                >
                  <option value="">Gewünschte Behandlung (optional)</option>
                  <option value="Hyaluron">Hyaluron</option>
                  <option value="Botox">Botox</option>
                  <option value="PRP">PRP Behandlung</option>
                  <option value="Skin">Skin & Regeneration</option>
                  <option value="Fadenlifting">Fadenlifting</option>
                  <option value="Infusionstherapie">Infusionstherapie</option>
                  <option value="Beratung">Allgemeine Beratung</option>
                </select>
              </div>

              <div>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Ihre Nachricht *"
                  required
                  rows={4}
                  data-testid="input-message"
                  className="form-input resize-none"
                />
              </div>

              <button
                type="submit"
                data-testid="submit-btn"
                disabled={isSubmitting}
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Wird gesendet..." : "Anfrage senden"}
              </button>

              <p className="text-xs text-[#A8A29E] text-center">
                * Pflichtfelder. Ihre Daten werden vertraulich behandelt.
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-[#1C1917] text-white py-12">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-3 gap-8 items-center">
          <div>
            <span className="font-['Playfair_Display'] text-2xl">Medical Beauty</span>
            <span className="text-[#D4AF37] ml-2 text-sm tracking-widest">NM</span>
          </div>

          <div className="text-center text-sm text-stone-400">
            © {new Date().getFullYear()} Medical Beauty NM. Alle Rechte vorbehalten.
          </div>

          <div className="flex justify-start md:justify-end gap-6 text-sm">
            <Link
              to="/impressum"
              data-testid="footer-impressum"
              className="text-stone-400 hover:text-[#D4AF37] transition-colors"
            >
              Impressum
            </Link>
            <Link
              to="/datenschutz"
              data-testid="footer-datenschutz"
              className="text-stone-400 hover:text-[#D4AF37] transition-colors"
            >
              Datenschutz
            </Link>
            <Link
              to="/agb"
              data-testid="footer-agb"
              className="text-stone-400 hover:text-[#D4AF37] transition-colors"
            >
              AGB
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Impressum Page
const ImpressumPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-32">
        <Link 
          to="/" 
          data-testid="back-to-home"
          className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#997B2F] transition-colors mb-8"
        >
          <ArrowLeft size={20} />
          <span>Zurück zur Startseite</span>
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl font-medium text-[#1C1917] mb-8">
            Impressum
          </h1>
          <div className="gold-line mb-12" />

          <div className="space-y-8 text-[#57534E]">
            <div className="bg-white p-8 border border-stone-100">
              <table className="w-full">
                <tbody className="divide-y divide-stone-100">
                  <tr>
                    <td className="py-4 font-medium text-[#1C1917] w-1/3">Name des Unternehmens</td>
                    <td className="py-4">Medical Beauty NM</td>
                  </tr>
                  <tr>
                    <td className="py-4 font-medium text-[#1C1917]">Inhaber</td>
                    <td className="py-4">Ntiana Meziridou</td>
                  </tr>
                  <tr>
                    <td className="py-4 font-medium text-[#1C1917]">Anschrift</td>
                    <td className="py-4">
                      An der Schmutter 16<br />
                      86690 Mertingen
                    </td>
                  </tr>
                  <tr>
                    <td className="py-4 font-medium text-[#1C1917]">Kontaktdaten</td>
                    <td className="py-4">
                      E-Mail: <a href="mailto:info@medicalbeauty-nm.de" className="text-[#D4AF37] hover:underline">info@medicalbeauty-nm.de</a>
                    </td>
                  </tr>
                  <tr>
                    <td className="py-4 font-medium text-[#1C1917]">Berufsbezeichnung</td>
                    <td className="py-4">Ärztin</td>
                  </tr>
                  <tr>
                    <td className="py-4 font-medium text-[#1C1917]">Umsatzsteuer-ID</td>
                    <td className="py-4">DE456928470</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="bg-white p-8 border border-stone-100">
              <h2 className="font-['Playfair_Display'] text-xl text-[#1C1917] mb-4">Haftungshinweis</h2>
              <p className="leading-relaxed">
                Trotz sorgfältiger Kontrolle übernehmen wir keine Haftung für externe Links. 
                Für den Inhalt der verlinkten Seiten sind ausschließlich deren Betreiber verantwortlich.
              </p>
            </div>

            <div className="bg-white p-8 border border-stone-100">
              <h2 className="font-['Playfair_Display'] text-xl text-[#1C1917] mb-4">EU-Streitschlichtung</h2>
              <p className="leading-relaxed mb-2">
                Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:{" "}
                <a 
                  href="https://ec.europa.eu/consumers/odr/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#D4AF37] hover:underline"
                >
                  https://ec.europa.eu/consumers/odr/
                </a>
              </p>
              <p className="leading-relaxed">
                Unsere E-Mail-Adresse finden Sie oben im Impressum.
              </p>
            </div>

            <div className="bg-white p-8 border border-stone-100">
              <h2 className="font-['Playfair_Display'] text-xl text-[#1C1917] mb-4">Verbraucherstreitbeilegung/Universalschlichtungsstelle</h2>
              <p className="leading-relaxed">
                Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer 
                Verbraucherschlichtungsstelle teilzunehmen.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
};

// Datenschutz Page
const DatenschutzPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-32">
        <Link 
          to="/" 
          data-testid="back-to-home-datenschutz"
          className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#997B2F] transition-colors mb-8"
        >
          <ArrowLeft size={20} />
          <span>Zurück zur Startseite</span>
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl font-medium text-[#1C1917] mb-8">
            Datenschutzerklärung
          </h1>
          <div className="gold-line mb-12" />

          <div className="space-y-8 text-[#57534E]">
            {/* Section 1 */}
            <div className="bg-white p-8 border border-stone-100">
              <h2 className="font-['Playfair_Display'] text-2xl text-[#1C1917] mb-4">
                1. Datenschutz auf einen Blick
              </h2>
              <h3 className="font-medium text-[#1C1917] mb-2">Allgemeine Hinweise</h3>
              <p className="leading-relaxed">
                Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren 
                personenbezogenen Daten passiert, wenn Sie diese Website besuchen. Personenbezogene 
                Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können.
              </p>
            </div>

            {/* Section 2 */}
            <div className="bg-white p-8 border border-stone-100">
              <h2 className="font-['Playfair_Display'] text-2xl text-[#1C1917] mb-4">
                2. Hosting und Content Delivery Networks (CDN)
              </h2>
              <h3 className="font-medium text-[#1C1917] mb-2">Externes Hosting</h3>
              <p className="leading-relaxed mb-4">
                Diese Website wird bei einem externen Dienstleister gehostet (Emergent). Die 
                personenbezogenen Daten, die auf dieser Website erfasst werden, werden auf den 
                Servern des Hosters gespeichert. Hierbei kann es sich v. a. um IP-Adressen, 
                Kontaktanfragen, Meta- und Kommunikationsdaten, Webseitenzugriffe und sonstige 
                Daten, die über eine Website generiert werden, handeln.
              </p>
              <p className="leading-relaxed">
                Der Einsatz des Hosters erfolgt zum Zwecke der Vertragserfüllung gegenüber unseren 
                potenziellen und bestehenden Kunden und im Interesse einer sicheren, schnellen und 
                effizienten Bereitstellung unseres Online-Angebots durch einen professionellen 
                Anbieter (Art. 6 Abs. 1 lit. b und f DSGVO).
              </p>
            </div>

            {/* Section 3 */}
            <div className="bg-white p-8 border border-stone-100">
              <h2 className="font-['Playfair_Display'] text-2xl text-[#1C1917] mb-4">
                3. Allgemeine Hinweise und Pflichtinformationen
              </h2>
              <h3 className="font-medium text-[#1C1917] mb-2">Verantwortliche Stelle</h3>
              <p className="leading-relaxed mb-4">
                Die verantwortliche Stelle für die Datenverarbeitung auf dieser Website ist:
              </p>
              <div className="bg-[#F5F2EB] p-4 mb-4">
                <p>
                  Ntiana Meziridou<br />
                  Medical Beauty NM<br />
                  An der Schmutter 16<br />
                  86690 Mertingen<br />
                  E-Mail: <a href="mailto:info@medicalbeauty-nm.de" className="text-[#D4AF37]">info@medicalbeauty-nm.de</a>
                </p>
              </div>
              <h3 className="font-medium text-[#1C1917] mb-2 mt-6">Widerruf Ihrer Einwilligung zur Datenverarbeitung</h3>
              <p className="leading-relaxed">
                Viele Datenverarbeitungsvorgänge sind nur mit Ihrer ausdrücklichen Einwilligung 
                möglich. Sie können eine bereits erteilte Einwilligung jederzeit widerrufen. Die 
                Rechtmäßigkeit der bis zum Widerruf erfolgten Datenverarbeitung bleibt vom Widerruf 
                unberührt.
              </p>
            </div>

            {/* Section 4 */}
            <div className="bg-white p-8 border border-stone-100">
              <h2 className="font-['Playfair_Display'] text-2xl text-[#1C1917] mb-4">
                4. Datenerfassung auf dieser Website
              </h2>
              <h3 className="font-medium text-[#1C1917] mb-2">SSL- bzw. TLS-Verschlüsselung</h3>
              <p className="leading-relaxed mb-6">
                Diese Seite nutzt aus Sicherheitsgründen und zum Schutz der Übertragung vertraulicher 
                Inhalte eine SSL- bzw. TLS-Verschlüsselung. Eine verschlüsselte Verbindung erkennen 
                Sie daran, dass die Adresszeile des Browsers von „http://" auf „https://" wechselt 
                und an dem Schloss-Symbol in Ihrer Browserzeile.
              </p>
              <h3 className="font-medium text-[#1C1917] mb-2">Server-Log-Dateien</h3>
              <p className="leading-relaxed mb-4">
                Der Provider der Seiten erhebt und speichert automatisch Informationen in sogenannten 
                Server-Log-Dateien, die Ihr Browser automatisch an uns übermittelt. Dies sind:
              </p>
              <ul className="list-disc list-inside space-y-1 mb-4">
                <li>Browsertyp und Browserversion</li>
                <li>verwendetes Betriebssystem</li>
                <li>Referrer URL</li>
                <li>Hostname des zugreifenden Rechners</li>
                <li>Uhrzeit der Serveranfrage</li>
                <li>IP-Adresse</li>
              </ul>
              <p className="leading-relaxed">
                Eine Zusammenführung dieser Daten mit anderen Datenquellen wird nicht vorgenommen.
              </p>
            </div>

            {/* Section 5 */}
            <div className="bg-white p-8 border border-stone-100">
              <h2 className="font-['Playfair_Display'] text-2xl text-[#1C1917] mb-4">
                5. Plugins und Tools
              </h2>
              <h3 className="font-medium text-[#1C1917] mb-2">Google Maps</h3>
              <p className="leading-relaxed">
                Diese Seite nutzt über eine Schnittstelle den Kartendienst Google Maps. Anbieter 
                ist die Google Ireland Limited. Zur Nutzung der Funktionen von Google Maps ist es 
                notwendig, Ihre IP-Adresse zu speichern. Diese Informationen werden in der Regel 
                an einen Server von Google in den USA übertragen und dort gespeichert. Der Anbieter 
                dieser Seite hat keinen Einfluss auf diese Datenübertragung.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
};

// AGB Page
const AGBPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-32">
        <Link
          to="/"
          data-testid="back-to-home-agb"
          className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#997B2F] transition-colors mb-8"
        >
          <ArrowLeft size={20} />
          <span>Zurück zur Startseite</span>
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl font-medium text-[#1C1917] mb-8">
            Allgemeine Geschäftsbedingungen
          </h1>
          <div className="gold-line mb-12" />

          <div className="space-y-8 text-[#57534E]">
            <div className="bg-white p-8 border border-stone-100">
              <h2 className="font-['Playfair_Display'] text-2xl text-[#1C1917] mb-4">
                § 1 Geltungsbereich & Vertragspartner
              </h2>
              <p className="leading-relaxed">
                Diese Allgemeinen Geschäftsbedingungen (AGB) gelten für alle Behandlungsverträge,
                Terminvereinbarungen und Beratungsleistungen zwischen der Praxis Medical Beauty NM
                (Inhaberin: Ärztin Ntiana Meziridou), An der Schmutter 16, 86690 Mertingen
                (nachfolgend „Praxis") und ihren Patienten bzw. Kunden (nachfolgend „Patient").
                Abweichende Bedingungen des Patienten werden nicht anerkannt, es sei denn, die
                Praxis stimmt ihrer Geltung ausdrücklich schriftlich zu.
              </p>
            </div>

            <div className="bg-white p-8 border border-stone-100">
              <h2 className="font-['Playfair_Display'] text-2xl text-[#1C1917] mb-4">
                § 2 Behandlungsvertrag & Aufklärung
              </h2>
              <p className="leading-relaxed mb-4">
                Der Behandlungsvertrag kommt durch die Vereinbarung eines Termins und die
                anschließende medizinische Aufklärung sowie Einwilligung zustande.
              </p>
              <p className="leading-relaxed mb-4">
                <strong className="text-[#1C1917]">Kein Erfolgsversprechen:</strong> Ästhetische
                Behandlungen sind medizinische Dienstleistungen. Ein bestimmter ästhetischer Erfolg
                kann rechtlich nicht garantiert werden, da das Ergebnis vom individuellen Gewebe
                und den körperlichen Reaktionen abhängt. Schuldengegenstand ist die fachgerechte
                Durchführung nach aktuellen medizinischen Standards.
              </p>
              <p className="leading-relaxed">
                <strong className="text-[#1C1917]">Mitwirkungspflicht:</strong> Der Patient ist
                verpflichtet, vor der Behandlung vollständige und wahrheitsgemäße Angaben zu
                Gesundheitszustand, Vorerkrankungen, Allergien, Medikamenteneinnahme sowie einer
                eventuellen Schwangerschaft oder Stillzeit zu machen.
              </p>
            </div>

            <div className="bg-white p-8 border border-stone-100">
              <h2 className="font-['Playfair_Display'] text-2xl text-[#1C1917] mb-4">
                § 3 Terminvereinbarung, Absagen & Ausfallhonorar
              </h2>
              <p className="leading-relaxed mb-4">
                <strong className="text-[#1C1917]">Bestellpraxis:</strong> Die Praxis wird als
                reine Bestellpraxis geführt. Termine werden exklusiv für den Patienten reserviert.
              </p>
              <p className="leading-relaxed mb-4">
                <strong className="text-[#1C1917]">Stornierungsfrist:</strong> Vereinbarte Termine
                müssen spätestens 24 Stunden vor dem Termin abgesagt werden (per E-Mail an{" "}
                <a href="mailto:info@medicalbeauty-nm.de" className="text-[#D4AF37] hover:underline">
                  info@medicalbeauty-nm.de
                </a>
                ).
              </p>
              <p className="leading-relaxed mb-4">
                <strong className="text-[#1C1917]">Ausfallpauschale:</strong> Bei einer Absage
                weniger als 24 Stunden vor dem Termin oder bei unentschuldigtem Nichterscheinen
                wird eine pauschalierte Ausfallentschädigung in Höhe von 100,00 € fällig.
              </p>
              <p className="leading-relaxed mb-4">
                Dem Patienten bleibt der Nachweis gestattet, dass ein Schaden überhaupt nicht
                entstanden oder wesentlich niedriger als die Pauschale ist. Die Pflicht zur Zahlung
                entfällt, wenn der Termin von der Praxis anderweitig vergeben werden konnte.
              </p>
              <p className="leading-relaxed">
                Bei verspätetem Erscheinen verkürzt sich die Behandlungszeit entsprechend, um den
                Folgeterminen nachzukommen. Der volle Behandlungspreis bleibt geschuldet.
              </p>
            </div>

            <div className="bg-white p-8 border border-stone-100">
              <h2 className="font-['Playfair_Display'] text-2xl text-[#1C1917] mb-4">
                § 4 Vergütung & Zahlungsbedingungen
              </h2>
              <p className="leading-relaxed mb-4">
                Die Vergütung richtet sich nach der Gebührenordnung für Ärzte (GOÄ) bzw. der
                vereinbarten Honorarvereinbarung für ästhetische Behandlungen.
              </p>
              <p className="leading-relaxed">
                Der Rechnungsbetrag ist unmittelbar im Anschluss an die Behandlungsdurchführung
                ohne Abzug fällig. Die Zahlung erfolgt vor Ort in bar oder per EC-/Kreditkarte.
              </p>
            </div>

            <div className="bg-white p-8 border border-stone-100">
              <h2 className="font-['Playfair_Display'] text-2xl text-[#1C1917] mb-4">
                § 5 Haftung & Nachsorge
              </h2>
              <p className="leading-relaxed mb-4">
                Die Praxis haftet für Personenschäden sowie für Schäden, die auf Vorsatz oder
                grober Fahrlässigkeit beruhen, nach den gesetzlichen Bestimmungen.
              </p>
              <p className="leading-relaxed">
                Für Schäden oder unerwünschte Reaktionen, die dadurch entstehen, dass der Patient
                die ärztlichen Verhaltensempfehlungen und Nachsorgehinweise (z. B. Verzicht auf
                Sport, Sauna, direkte Sonne) missachtet hat, übernimmt die Praxis keine Haftung.
              </p>
            </div>

            <div className="bg-white p-8 border border-stone-100">
              <h2 className="font-['Playfair_Display'] text-2xl text-[#1C1917] mb-4">
                § 6 Gutscheine
              </h2>
              <p className="leading-relaxed">
                Gutscheine der Praxis sind im Rahmen der gesetzlichen Verjährungsfrist von 3 Jahren
                ab Ausstellungsdatum gültig. Eine Barauszahlung von Gutscheinwerten ist
                ausgeschlossen.
              </p>
            </div>

            <div className="bg-white p-8 border border-stone-100">
              <h2 className="font-['Playfair_Display'] text-2xl text-[#1C1917] mb-4">
                § 7 Schlussbestimmungen
              </h2>
              <p className="leading-relaxed">
                Sollten einzelne Bestimmungen dieser AGB unwirksam sein oder werden, bleibt die
                Wirksamkeit der übrigen Bestimmungen unberührt. Es gilt das Recht der
                Bundesrepublik Deutschland.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
};

// Main App Component
function App() {
  const [treatments, setTreatments] = useState([]);

  useEffect(() => {
    const fetchTreatments = async () => {
      try {
        const response = await axios.get(`${API}/treatments`);
        setTreatments(response.data);
      } catch (error) {
        console.error("Error fetching treatments:", error);
      }
    };
    fetchTreatments();
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={
          <div className="App" data-testid="app-container">
            <Navigation />
            <HeroSection />
            <TreatmentsSection treatments={treatments} />
            <AboutSection />
            <GallerySection />
            <ContactSection />
            <Footer />
          </div>
        } />
        <Route path="/impressum" element={<ImpressumPage />} />
        <Route path="/datenschutz" element={<DatenschutzPage />} />
        <Route path="/agb" element={<AGBPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
